import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate, fmtDateTime as _fmtDateTime } from '@/lib/dates';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ClipboardCheck, Trash2, RefreshCw, Star, Eye, Download, FileText } from 'lucide-react';

const SERVICE_TYPES = [
  'Cleaning',
  'Landscaping',
  'Facility Maintenance',
  'Security',
  'Porter',
  'Parking Garage',
  'Pressure Washing',
  'Other'
];

const InspectionManagement = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    customer: '',
    location: '',
    inspector: '',
    serviceType: '',
    templateType: '',
    rating: '',
    hasDeficiencies: '',
    dateFrom: '',
    dateTo: ''
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState(null);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/cleaning-inspections`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (inspection) => {
    setInspectionToDelete(inspection);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!inspectionToDelete) return;
    
    try {
      await axios.delete(`${API}/cleaning-inspections/${inspectionToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Inspection deleted successfully');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
      fetchInspections();
    } catch (error) {
      toast.error('Failed to delete inspection');
      setShowDeleteDialog(false);
      setInspectionToDelete(null);
    }
  };

  const downloadPDF = async (inspection) => {
    try {
      const response = await axios.get(`${API}/inspections/${inspection.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.pdf_url) {
        // Backend returns a URL to the generated PDF
        const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${response.data.pdf_url}`;
        
        // Open PDF in new tab and trigger download
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.setAttribute('download', `Inspection_${inspection.inspection_number}.pdf`);
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        link.remove();
        
        toast.success('PDF downloaded successfully');
      } else {
        throw new Error('PDF URL not provided by server');
      }
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to download PDF');
    }
  };

  const viewDetails = (inspection) => {
    setSelectedInspection(inspection);
    setShowViewDialog(true);
  };

  const getRatingLabel = (rating) => {
    const labels = {
      1: 'Poor',
      2: 'Fair',
      3: 'Good',
      4: 'Very Good',
      5: 'Excellent'
    };
    return labels[rating] || 'N/A';
  };

  const getRatingColor = (rating) => {
    if (rating === 5) return 'text-green-600';
    if (rating === 4) return 'text-blue-600';
    if (rating === 3) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getWeeklyComplianceStatus = (inspection) => {
    const fauRecCenterNames = ["FAU Rec Center", "FAU Recreation Center", "Florida Atlantic University Rec Center"];
    const locationName = inspection.location_name || '';
    
    if (!fauRecCenterNames.some(name => locationName.includes(name))) {
      return null; // Not FAU Rec Center
    }

    const today = new Date();
    const inspectionDate = new Date(inspection.inspection_date);
    const daysDiff = Math.floor((today - inspectionDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff <= 7) {
      return { status: 'compliant', label: 'Weekly Compliant', color: 'bg-green-100 text-green-800 border-green-300' };
    } else if (daysDiff <= 10) {
      return { status: 'due_soon', label: 'Due Soon', color: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
    } else {
      return { status: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-800 border-red-300' };
    }
  };

  const filteredInspections = inspections.filter(inspection => {
    if (filters.customer && !inspection.customer_name?.toLowerCase().includes(filters.customer.toLowerCase())) return false;
    if (filters.location && !inspection.location_name?.toLowerCase().includes(filters.location.toLowerCase())) return false;
    if (filters.inspector && !inspection.inspector_name?.toLowerCase().includes(filters.inspector.toLowerCase())) return false;
    if (filters.serviceType && filters.serviceType !== 'all' && inspection.service_type !== filters.serviceType) return false;
    if (filters.templateType && filters.templateType !== 'all') {
      const areaType = inspection.area_type?.toLowerCase() || '';
      if (!areaType.includes(filters.templateType.toLowerCase())) return false;
    }
    if (filters.rating && inspection.cleanliness_rating !== parseInt(filters.rating)) return false;
    if (filters.hasDeficiencies && filters.hasDeficiencies !== 'all') {
      const hasDeficiencies = inspection.has_deficiencies || inspection.cleanliness_rating <= 2;
      if (filters.hasDeficiencies === 'yes' && !hasDeficiencies) return false;
      if (filters.hasDeficiencies === 'no' && hasDeficiencies) return false;
    }
    if (filters.dateFrom && new Date(inspection.inspection_date) < new Date(filters.dateFrom)) return false;
    if (filters.dateTo && new Date(inspection.inspection_date) > new Date(filters.dateTo)) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      
      
      <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <ClipboardCheck className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold">Inspection Management</h2>
        </div>
        <Button onClick={fetchInspections} variant="outline">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="mb-4">
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-5 gap-4 text-center">
              <div>
                <p className="text-2xl font-bold" style={{ color: '#6B9F3E' }}>{inspections.length}</p>
                <p className="text-sm text-gray-500">Total Inspections</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{inspections.filter(i => i.cleanliness_rating === 5).length}</p>
                <p className="text-sm text-gray-500">Excellent (5)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-blue-600">{inspections.filter(i => i.cleanliness_rating === 4).length}</p>
                <p className="text-sm text-gray-500">Very Good (4)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-600">{inspections.filter(i => i.cleanliness_rating <= 3).length}</p>
                <p className="text-sm text-gray-500">Needs Improvement (1-3)</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-red-600">{inspections.filter(i => i.has_deficiencies || i.cleanliness_rating <= 2).length}</p>
                <p className="text-sm text-gray-500">Has Deficiencies</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="text-lg">Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div>
              <Label className="text-xs">Customer</Label>
              <Input
                placeholder="Search customer..."
                value={filters.customer}
                onChange={(e) => setFilters({...filters, customer: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Location</Label>
              <Input
                placeholder="Search location..."
                value={filters.location}
                onChange={(e) => setFilters({...filters, location: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Inspector</Label>
              <Input
                placeholder="Search inspector..."
                value={filters.inspector}
                onChange={(e) => setFilters({...filters, inspector: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Service Type</Label>
              <Select value={filters.serviceType || 'all'} onValueChange={(value) => setFilters({...filters, serviceType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {SERVICE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Rating (1-5)</Label>
              <Select value={filters.rating || 'all'} onValueChange={(value) => setFilters({...filters, rating: value === 'all' ? '' : value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Ratings" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Ratings</SelectItem>
                  <SelectItem value="5">5 - Excellent</SelectItem>
                  <SelectItem value="4">4 - Very Good</SelectItem>
                  <SelectItem value="3">3 - Good</SelectItem>
                  <SelectItem value="2">2 - Fair</SelectItem>
                  <SelectItem value="1">1 - Poor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Template Type</Label>
              <Select value={filters.templateType || 'all'} onValueChange={(value) => setFilters({...filters, templateType: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All Templates" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Templates</SelectItem>
                  <SelectItem value="bank">Bank Branch</SelectItem>
                  <SelectItem value="commercial">Commercial Office</SelectItem>
                  <SelectItem value="school">School Campus</SelectItem>
                  <SelectItem value="rec">Rec Center</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Has Deficiencies</Label>
              <Select value={filters.hasDeficiencies || 'all'} onValueChange={(value) => setFilters({...filters, hasDeficiencies: value})}>
                <SelectTrigger>
                  <SelectValue placeholder="All" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Yes</SelectItem>
                  <SelectItem value="no">No</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Date From</Label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
              />
            </div>
            <div>
              <Label className="text-xs">Date To</Label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
              />
            </div>
            <div className="flex items-end">
              <Button 
                variant="outline" 
                onClick={() => setFilters({customer: '', location: '', inspector: '', serviceType: '', templateType: '', rating: '', hasDeficiencies: '', dateFrom: '', dateTo: ''})}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-600">
            Showing {filteredInspections.length} of {inspections.length} inspections
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Inspections</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredInspections.length === 0 ? (
            <div className="text-center py-12">
              <ClipboardCheck className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              <p className="text-gray-500">No inspections found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50">
                    <th className="text-left py-3 px-4 font-medium">Inspection #</th>
                    <th className="text-left py-3 px-4 font-medium">Date</th>
                    <th className="text-left py-3 px-4 font-medium">Service Type</th>
                    <th className="text-left py-3 px-4 font-medium">Customer</th>
                    <th className="text-left py-3 px-4 font-medium">Location</th>
                    <th className="text-left py-3 px-4 font-medium">Inspector</th>
                    <th className="text-left py-3 px-4 font-medium">Rating</th>
                    <th className="text-left py-3 px-4 font-medium">POA Status</th>
                    <th className="text-right py-3 px-4 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInspections.map((inspection) => (
                    <tr key={inspection.id} className="border-b hover:bg-gray-50">
                      <td className="py-3 px-4 font-medium">{inspection.inspection_number}</td>
                      <td className="py-3 px-4">{_fmtDate(inspection.inspection_date)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline">{inspection.service_type || 'Cleaning'}</Badge>
                      </td>
                      <td className="py-3 px-4">{inspection.customer_name}</td>
                      <td className="py-3 px-4">
                        <div className="flex flex-col gap-1">
                          <span>{inspection.location_name}</span>
                          {getWeeklyComplianceStatus(inspection) && (
                            <Badge className={`text-xs ${getWeeklyComplianceStatus(inspection).color}`}>
                              📅 {getWeeklyComplianceStatus(inspection).label}
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4">{inspection.inspector_name}</td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Star className={`h-4 w-4 ${getRatingColor(inspection.cleanliness_rating)}`} fill="currentColor" />
                          <span className={`font-semibold ${getRatingColor(inspection.cleanliness_rating)}`}>
                            {inspection.cleanliness_rating}
                          </span>
                          <span className="text-gray-500 text-xs">({getRatingLabel(inspection.cleanliness_rating)})</span>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        {(inspection.has_deficiencies || inspection.cleanliness_rating <= 2) ? (
                          <Badge className="bg-red-100 text-red-800 border-red-300">
                            POA Required
                          </Badge>
                        ) : inspection.cleanliness_rating === 3 ? (
                          <Badge className="bg-yellow-100 text-yellow-800 border-yellow-300">
                            Optional
                          </Badge>
                        ) : (
                          <Badge className="bg-green-100 text-green-800 border-green-300">
                            Not Required
                          </Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewDetails(inspection)}
                            title="View Details"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPDF(inspection)}
                            title="Download PDF"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            PDF
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(inspection)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inspection Details</DialogTitle>
          </DialogHeader>
          {selectedInspection && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Inspection Number</Label>
                  <p className="text-base">{selectedInspection.inspection_number}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Service Type</Label>
                  <p className="text-base">{selectedInspection.service_type || 'Cleaning'}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Date</Label>
                  <p className="text-base">{_fmtDate(selectedInspection.inspection_date)}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Rating</Label>
                  <div className="flex items-center gap-2">
                    <Star className={`h-5 w-5 ${getRatingColor(selectedInspection.cleanliness_rating)}`} fill="currentColor" />
                    <span className={`font-semibold ${getRatingColor(selectedInspection.cleanliness_rating)}`}>
                      {selectedInspection.cleanliness_rating} - {getRatingLabel(selectedInspection.cleanliness_rating)}
                    </span>
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Customer</Label>
                  <p className="text-base">{selectedInspection.customer_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Location</Label>
                  <p className="text-base">{selectedInspection.location_name}</p>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Inspector</Label>
                  <p className="text-base">{selectedInspection.inspector_name}</p>
                </div>
              </div>

              {selectedInspection.notes && (
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Notes</Label>
                  <p className="text-base p-3 bg-gray-50 rounded">{selectedInspection.notes}</p>
                </div>
              )}

              {selectedInspection.issues_found && (
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Issues Found</Label>
                  <p className="text-base p-3 bg-orange-50 rounded">{selectedInspection.issues_found}</p>
                </div>
              )}

              {selectedInspection.corrective_actions && (
                <div>
                  <Label className="text-sm font-semibold text-gray-600">Corrective Actions</Label>
                  <p className="text-base p-3 bg-blue-50 rounded">{selectedInspection.corrective_actions}</p>
                </div>
              )}

              {selectedInspection.photos && selectedInspection.photos.length > 0 && (
                <div>
                  <Label className="text-sm font-semibold text-gray-600 mb-2">Photos ({selectedInspection.photos.length})</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {selectedInspection.photos.map((photo, idx) => (
                      <img key={idx} src={photo} alt={`Photo ${idx + 1}`} className="w-full h-32 object-cover rounded" />
                    ))}
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-500 pt-4 border-t">
                Submitted: {_fmtDateTime(selectedInspection.submitted_at)}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to delete inspection "{inspectionToDelete?.inspection_number}"? This action cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={confirmDelete}>
              Delete Inspection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default InspectionManagement;
